import { ReactNode, useEffect, useRef } from 'react';
import { useTranslate } from '@prenuvo/halo';

import { api } from '@/api';
import { useToastHandler } from '@/shared/hook';
import { Loading } from '@/shared/ui';
import { redirectToLogin } from '@/shared/util';
import { useAuthStore } from '@/store';

import { RoleGuard } from './role.guard';

interface UserStateProps {
  children: ReactNode;
}

export function UserState({ children }: UserStateProps) {
  const toastRef = useRef<HTMLSpanElement>(null);

  const translate = useTranslate();

  const showUnauthenticatedToast = useAuthStore((state) => state.showUnauthenticatedToast);

  const toastHandler = useToastHandler({
    description: 'FCT.AUTHENTICATION.ERROR.DESCRIPTION',
    title: translate('FCT.AUTHENTICATION.ERROR.TITLE'),
    toastRef,
  });

  const { data, error, isError, isLoading } = api.pii.user.query.useGetUser();

  useEffect(() => {
    if (showUnauthenticatedToast) {
      toastHandler();
    }
  }, [showUnauthenticatedToast, toastHandler]);

  if (isLoading) {
    return <Loading />;
  }

  if (error && isError) {
    // Don't redirect if error interceptor is handling it (showUnauthenticatedToast is true)
    // This allows the error interceptor's delayed redirect to work
    if (!showUnauthenticatedToast) {
      redirectToLogin();
    }

    return null;
  }

  return <RoleGuard user={data!.user}>{children}</RoleGuard>;
}
